import { useState, useEffect } from 'react';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO, { generateFAQSchema } from '../components/SEO';
import { faqAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { getField } from '../lib/localize';

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(null);
  const { t, language } = useLanguage();
  const { isDark } = useTheme();

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const res = await faqAPI.getAll();
        setFaqs(res.data);
      } catch (err) {
        console.error('Failed to fetch FAQs:', err);
      }
      setLoading(false);
    };
    fetchFAQs();
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Static FAQ content if API returns empty
  const staticFAQs = [
    {
      question: "নামজারি না করলে কি সমস্যা হয়?",
      question_en: "What problems arise if Namjari is not done?",
      answer: "নামজারি না করলে আপনি জমির আইনগত মালিক হিসেবে স্বীকৃত হবেন না। ভবিষ্যতে জমি বিক্রি, ব্যাংক লোন বা অন্য কোনো লেনদেনে সমস্যা হবে।",
      answer_en: "Without Namjari, you will not be recognized as the legal owner. In the future, you will face problems selling land, getting bank loans, or any other transactions.",
      category: "namjari"
    },
    {
      question: "খতিয়ান ও পোর্চার মধ্যে পার্থক্য কী?",
      question_en: "What is the difference between Khatian and Porcha?",
      answer: "খতিয়ান হলো জমির মালিকানার মূল রেকর্ড যা সরকার সংরক্ষণ করে। পোর্চা হলো খতিয়ানের সার্টিফাইড কপি যা আপনি সংগ্রহ করতে পারেন।",
      answer_en: "Khatian is the original ownership record maintained by the government. Porcha is a certified copy of the khatian that you can collect.",
      category: "khotiyan"
    },
    {
      question: "জমি কেনার আগে কী কী যাচাই করতে হবে?",
      question_en: "What should be verified before buying land?",
      answer: "দলিলের সত্যতা, খতিয়ানে নাম, জমির প্রকৃত দখল, মামলা-মোকদ্দমা আছে কিনা এবং ব্যাংকে বন্ধক আছে কিনা যাচাই করুন।",
      answer_en: "Verify the deed's authenticity, name in khatian, actual land possession, any pending lawsuits, and whether it is mortgaged to a bank.",
      category: "general"
    },
    {
      question: "অনলাইনে পোর্চা কিভাবে পাব?",
      question_en: "How can I get Porcha online?",
      answer: "land.gov.bd ওয়েবসাইটে গিয়ে 'ই-পোর্চা' অপশনে ক্লিক করুন। প্রয়োজনীয় তথ্য দিয়ে আবেদন করলে অনলাইনে পোর্চা পেয়ে যাবেন।",
      answer_en: "Go to land.gov.bd website and click 'e-Porcha' option. Fill in the required information and you will get the Porcha online.",
      category: "general"
    },
    {
      question: "দলিল ছাড়া জমি কি বিক্রি করা যায়?",
      question_en: "Can land be sold without a deed?",
      answer: "না, দলিল ছাড়া জমি আইনগতভাবে বিক্রি করা যায় না। দলিল হলো জমি মালিকানার প্রধান প্রমাণপত্র।",
      answer_en: "No, land cannot be legally sold without a deed. The deed is the primary proof of land ownership.",
      category: "legal"
    },
    {
      question: "জমির রেজিস্ট্রেশন ফি কত?",
      question_en: "What is the land registration fee?",
      answer: "সাধারণত জমির মূল্যের ৩% রেজিস্ট্রেশন ফি এবং ১.৫% স্ট্যাম্প ডিউটি দিতে হয়। এছাড়া উৎস কর এবং অন্যান্য ফি প্রযোজ্য।",
      answer_en: "Generally, 3% registration fee and 1.5% stamp duty of the land value is required. Additionally, source tax and other fees apply.",
      category: "fees"
    },
    {
      question: "ওয়ারিশ সম্পত্তি ভাগ করতে কী করতে হবে?",
      question_en: "What is needed to divide inherited property?",
      answer: "প্রথমে মৃত ব্যক্তির মৃত্যু সনদ সংগ্রহ করুন। তারপর ওয়ারিশ সনদ নিয়ে আইনানুযায়ী সম্পত্তি ভাগ করুন এবং নামজারি করুন।",
      answer_en: "First collect the deceased person's death certificate. Then divide the property according to law with the succession certificate and apply for Namjari.",
      category: "legal"
    },
    {
      question: "জমি জাল হলে কী করব?",
      question_en: "What should I do if land documents are forged?",
      answer: "দ্রুত থানায় জিডি করুন এবং আইনজীবীর পরামর্শ নিন। প্রয়োজনে দেওয়ানি মামলা করুন। সব প্রমাণ সংরক্ষণ করুন।",
      answer_en: "File a GD at the police station immediately and consult a lawyer. File a civil case if necessary. Preserve all evidence.",
      category: "legal"
    }
  ];

  const displayFAQs = faqs.length > 0 ? faqs : staticFAQs;
  
  // Prepare FAQ data for schema
  const faqSchemaData = displayFAQs.map(faq => ({
    question: language === 'en' ? getField(faq, 'question', language) : faq.question,
    answer: language === 'en' ? getField(faq, 'answer', language) : faq.answer,
  }));

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
      <SEO 
        title={language === 'en' ? 'FAQ - Frequently Asked Questions' : 'সচরাচর জিজ্ঞাসা (FAQ)'}
        description={language === 'en'
          ? 'Frequently asked questions about land registration, Namjari, Khatian, deeds in Bangladesh. Get answers to your land-related queries.'
          : 'বাংলাদেশে জমি রেজিস্ট্রেশন, নামজারি, খতিয়ান, দলিল সম্পর্কে সচরাচর জিজ্ঞাসা। আপনার ভূমি সংক্রান্ত প্রশ্নের উত্তর পান।'
        }
        path="/faq"
        schema={generateFAQSchema(faqSchemaData)}
      />
      <Navbar isDark={isDark} />

      {/* Header */}
      <section className={`pt-24 pb-12 border-b ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${isDark ? 'bg-[#2E7D32]/20' : 'bg-[#E8F5E9]'}`}>
              <HelpCircle className="text-[#2E7D32]" size={32} />
            </div>
            <h1 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="faq-title">
              {t('faq.title')}
            </h1>
            <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('faq.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={`rounded-xl p-6 animate-pulse ${isDark ? 'bg-[#111]' : 'bg-white'}`}>
                  <div className={`h-6 rounded w-3/4 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {displayFAQs.map((faq, index) => (
                <div
                  key={faq.id || index}
                  className={`rounded-xl border overflow-hidden ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'}`}
                  data-testid={`faq-${index}`}
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className={`w-full px-6 py-5 flex items-center justify-between text-left transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                  >
                    <span className={`text-lg font-medium pr-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{getField(faq, 'question', language)}</span>
                    {openIndex === index ? (
                      <ChevronUp className="text-[#2E7D32] flex-shrink-0" size={24} />
                    ) : (
                      <ChevronDown className={`flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} size={24} />
                    )}
                  </button>
                  {openIndex === index && (
                    <div className="px-6 pb-5">
                      <p className={`leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{getField(faq, 'answer', language)}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still have questions */}
      <section className={`py-16 ${isDark ? 'bg-[#0D330F]' : 'bg-[#E8F5E9]'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('faq.stillHaveQuestions')}
          </h2>
          <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            {t('faq.contactUs')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/contact">
              <button className={`px-8 py-3 rounded-full font-semibold transition-colors ${isDark ? 'bg-white text-[#0D330F] hover:bg-gray-100' : 'bg-[#2E7D32] text-white hover:bg-[#1B5E20]'}`}>
                {t('nav.contact')}
              </button>
            </a>
            <a href="/services">
              <button className="bg-[#FBC02D] text-gray-900 px-8 py-3 rounded-full font-semibold hover:bg-[#F9A825] transition-colors">
                {t('landing.findProviders')}
              </button>
            </a>
          </div>
        </div>
      </section>

      <Footer isDark={isDark} />
    </div>
  );
};

export default FAQPage;
